import type { HowItWorksStep, Benefit, FaqItem } from './types';

// ─── Target-size templates ──────────────────────────────────────────────────

interface TargetSizeContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function targetSizeTemplate(size: string, keyword: 'imagen' | 'foto'): TargetSizeContent {
  const kw = keyword === 'foto' ? 'foto' : 'imagen';
  const kwPlural = keyword === 'foto' ? 'fotos' : 'imágenes';
  const kwUpper = keyword === 'foto' ? 'Foto' : 'Imagen';

  // Determine context based on size for unique content
  const sizeNum = parseFloat(size);
  const unit = size.toLowerCase().includes('mb') ? 'MB' : 'KB';
  const isSmall = unit === 'KB' && sizeNum <= 75;
  const isMedium = unit === 'KB' && sizeNum > 75 && sizeNum <= 500;
  const isLarge = unit === 'MB';

  const contextPhrase = isSmall
    ? 'firmas digitales, avatares y formularios con requisitos estrictos'
    : isMedium
    ? 'formularios online, trámites y plataformas web'
    : 'plataformas de email, portfolios y CMS';

  const qualityNote = isSmall
    ? 'Para mejores resultados con tamaños pequeños, recomendamos reducir también las dimensiones de la imagen.'
    : isMedium
    ? `A ${size}, la calidad visual se mantiene excelente para la mayoría de usos digitales.`
    : `${size} permite mantener una calidad fotográfica muy alta, ideal para uso profesional.`;

  return {
    title: `Reducir ${kw} a ${size}`,
    h1: `Reducir ${kw} a ${size} online gratis`,
    metaTitle: `Reducir ${kw} a ${size} Online Gratis — Comprime al tamaño exacto | CompresorJPEG`,
    metaDescription: `Reduce el tamaño de tus ${kwPlural} a ${size} o menos online gratis. Perfecto para ${contextPhrase}. Sin registro.`,
    subtitle: `Comprime tus ${kwPlural} para que pesen ${size} o menos.`,
    introParagraph: `¿Necesitas que tu ${kw} pese menos de ${size}? Es un requisito habitual en ${contextPhrase}. Con nuestra herramienta puedes reducir cualquier ${kw} al tamaño requerido de forma rápida y gratuita, directamente desde tu navegador.`,
    seoIntro: `Reducir una ${kw} a ${size} es una necesidad frecuente para ${contextPhrase}. ${qualityNote} Nuestra herramienta de compresión te permite ajustar la calidad hasta alcanzar exactamente ${size} o menos, compatible con archivos JPG, PNG y WebP.`,
    howItWorksSteps: [
      { number: '1', title: `Sube tu ${kw}`, description: `Arrastra y suelta la ${kw} que necesitas reducir a ${size}.` },
      { number: '2', title: 'Ajusta la compresión', description: `Mueve el control de calidad hasta que el tamaño sea menor a ${size}.` },
      { number: '3', title: 'Descarga el resultado', description: `Descarga tu ${kw} reducida lista para usar donde la necesites.` },
    ],
    benefits: selectBenefits(size, kw, kwPlural, contextPhrase, isSmall, isMedium),
    faqItems: selectTargetSizeFaqs(size, kw, kwPlural, isSmall, isMedium, isLarge),
  };
}

function selectBenefits(size: string, kw: string, kwPlural: string, context: string, isSmall: boolean, isMedium: boolean): Benefit[] {
  const pool: Benefit[] = [
    { title: 'Tamaño exacto', description: `Controla la compresión para alcanzar exactamente el límite de ${size}.` },
    { title: `Ideal para ${isSmall ? 'firmas e iconos' : isMedium ? 'formularios' : 'email y web'}`, description: `Perfecto para cumplir los requisitos de ${context}.` },
    { title: 'Múltiples formatos', description: `Funciona con JPG, PNG y WebP para adaptarse a cualquier requisito de ${kw}.` },
    { title: 'Vista previa en tiempo real', description: `Verifica cómo queda tu ${kw} antes de descargarla.` },
    { title: 'Procesamiento local', description: `Tus ${kwPlural} se procesan en tu navegador sin subirse a ningún servidor.` },
    { title: 'Sin registro necesario', description: `Comprime tus ${kwPlural} de forma gratuita sin crear una cuenta.` },
  ];
  // Return first 4
  return pool.slice(0, 4);
}

function selectTargetSizeFaqs(size: string, kw: string, kwPlural: string, isSmall: boolean, isMedium: boolean, isLarge: boolean): FaqItem[] {
  const faqs: FaqItem[] = [
    {
      question: `¿Cómo reduzco una ${kw} a ${size}?`,
      answer: `Sube tu ${kw} y ajusta el control de calidad. El tamaño resultante se muestra en tiempo real para que puedas alcanzar exactamente ${size} o menos.`,
    },
    {
      question: `¿Se pierde calidad al reducir a ${size}?`,
      answer: isSmall
        ? `Depende de las dimensiones originales. Para ${kwPlural} pequeñas la calidad se mantiene aceptable. Para mejores resultados, reduce también las dimensiones.`
        : isMedium
        ? `La pérdida es mínima. A ${size}, la calidad visual es más que suficiente para documentos, formularios y uso web.`
        : `Prácticamente no. ${size} es un tamaño generoso que permite mantener una calidad excelente para la mayoría de usos.`,
    },
    {
      question: `¿Qué formatos puedo reducir a ${size}?`,
      answer: `Puedes reducir ${kwPlural} en formato JPG, PNG y WebP. El formato JPG suele ofrecer los mejores resultados para tamaños pequeños.`,
    },
    {
      question: `¿Es seguro comprimir mis ${kwPlural} aquí?`,
      answer: `Sí, completamente. Todas las ${kwPlural} se procesan localmente en tu navegador y nunca se suben a nuestros servidores. Tu privacidad está garantizada.`,
    },
  ];
  return faqs;
}

// ─── Compression synonym templates ──────────────────────────────────────────

interface CompressionContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function compressionSynonymTemplate(keyword: string, displayKeyword: string): CompressionContent {
  return {
    title: displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1),
    h1: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} — herramienta online gratuita`,
    metaTitle: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} Online Gratis — Sin perder calidad | CompresorJPEG`,
    metaDescription: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} online gratis. Reduce el peso de JPG, PNG y WebP sin perder calidad. Procesamiento local, rápido y sin registro.`,
    subtitle: `Reduce el peso de tus imágenes de forma rápida, segura y gratuita.`,
    introParagraph: `Nuestra herramienta te permite ${displayKeyword} de forma instantánea. Funciona con archivos JPG, PNG y WebP directamente en tu navegador, sin necesidad de instalar software ni crear una cuenta. El procesamiento es completamente local: tus archivos nunca salen de tu dispositivo.`,
    seoIntro: `Si buscas ${displayKeyword}, has llegado al lugar indicado. CompresorJPEG es una herramienta 100% online y gratuita que analiza cada imagen para encontrar el nivel de compresión óptimo. Reduce el peso de tus archivos hasta un 90% sin pérdida visual perceptible, ideal para optimizar tu web, enviar archivos por email o ahorrar espacio de almacenamiento.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tus imágenes', description: 'Arrastra y suelta tus archivos en cualquier formato: JPG, PNG o WebP.' },
      { number: '2', title: 'Compresión automática', description: 'Nuestro algoritmo analiza cada imagen y aplica la compresión ideal.' },
      { number: '3', title: 'Descarga los resultados', description: 'Descarga tus imágenes comprimidas individualmente o todas en un ZIP.' },
    ],
    benefits: [
      { title: 'Sin pérdida visible', description: 'Algoritmos inteligentes que reducen el peso sin afectar la calidad visual perceptible.' },
      { title: '100% en el navegador', description: 'Todo el procesamiento ocurre en tu dispositivo. Tus imágenes nunca se suben a ningún servidor.' },
      { title: 'Sin registro ni límites', description: 'Comprime todas las imágenes que necesites sin crear cuenta ni limitaciones.' },
      { title: 'Múltiples archivos a la vez', description: 'Sube y comprime varias imágenes simultáneamente para ahorrar tiempo.' },
    ],
    faqItems: [
      {
        question: `¿Es gratis ${displayKeyword}?`,
        answer: 'Sí, nuestra herramienta es completamente gratuita. No hay costos ocultos, marcas de agua ni limitaciones en la cantidad de imágenes.',
      },
      {
        question: '¿Se pierde calidad al comprimir?',
        answer: 'La pérdida de calidad es mínima e imperceptible en la mayoría de los casos. Puedes ajustar el nivel de compresión según tus necesidades de calidad.',
      },
      {
        question: '¿Qué formatos de imagen son compatibles?',
        answer: 'Nuestra herramienta es compatible con JPG, JPEG, PNG y WebP, los formatos más utilizados en fotografía digital y diseño web.',
      },
      {
        question: '¿Necesito instalar algún programa?',
        answer: 'No, la herramienta funciona completamente online en tu navegador web. No necesitas descargar ni instalar nada.',
      },
    ],
  };
}

// ─── Use-case templates ─────────────────────────────────────────────────────

interface UseCaseContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function useCaseTemplate(useCase: string, contextLabel: string, detailText: string): UseCaseContent {
  const isOptimization = useCase.startsWith('optimizar');

  const verb = isOptimization ? 'Optimizar' : 'Comprimir';
  const verbLower = isOptimization ? 'optimizar' : 'comprimir';
  const noun = isOptimization ? 'optimización' : 'compresión';

  return {
    title: `${verb} imagen para ${contextLabel}`,
    h1: `${verb} imágenes para ${contextLabel} online gratis`,
    metaTitle: `${verb} imagen para ${contextLabel} Online Gratis | CompresorJPEG`,
    metaDescription: `${verb} tus imágenes para ${contextLabel}. ${detailText} Herramienta gratuita online sin registro.`,
    subtitle: `${isOptimization ? 'Optimiza' : 'Reduce el peso de'} tus imágenes para ${contextLabel.toLowerCase()}.`,
    introParagraph: `${detailText} Con nuestra herramienta puedes ${verbLower} tus imágenes de forma rápida y precisa, directamente desde tu navegador y sin perder la calidad necesaria.`,
    seoIntro: `La ${noun} de imágenes para ${contextLabel.toLowerCase()} es esencial para obtener los mejores resultados. Nuestra herramienta analiza cada imagen y aplica el nivel de ${noun} óptimo, asegurando que tus archivos cumplan con los requisitos de tamaño y calidad de la plataforma de destino.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tus imágenes', description: `Arrastra y suelta las imágenes que necesitas ${verbLower} para ${contextLabel.toLowerCase()}.` },
      { number: '2', title: `${isOptimization ? 'Optimización' : 'Compresión'} inteligente`, description: `Nuestro algoritmo aplica la ${noun} ideal para ${contextLabel.toLowerCase()}.` },
      { number: '3', title: 'Descarga y usa', description: `Descarga tus imágenes ${isOptimization ? 'optimizadas' : 'comprimidas'} listas para usar.` },
    ],
    benefits: [
      { title: `Ideal para ${contextLabel}`, description: `${isOptimization ? 'Optimización' : 'Compresión'} ajustada a los requisitos de ${contextLabel.toLowerCase()}.` },
      { title: 'Calidad preservada', description: `Mantiene la calidad visual necesaria para que tus imágenes se vean profesionales.` },
      { title: 'Procesamiento local', description: 'Tus imágenes se procesan en tu navegador sin subirse a ningún servidor externo.' },
      { title: 'Sin registro ni instalación', description: 'Funciona directamente en el navegador, gratis y sin crear cuenta.' },
    ],
    faqItems: [
      {
        question: `¿Qué tamaño deben tener las imágenes para ${contextLabel.toLowerCase()}?`,
        answer: `Depende de la plataforma o uso específico. Como regla general, imágenes entre 100KB y 500KB ofrecen un buen equilibrio entre calidad y peso para la mayoría de usos.`,
      },
      {
        question: `¿Se pierde calidad al ${verbLower} para ${contextLabel.toLowerCase()}?`,
        answer: `La pérdida es mínima e imperceptible. Nuestra herramienta busca el mejor equilibrio entre tamaño y calidad para cada imagen.`,
      },
      {
        question: `¿Puedo ${verbLower} varias imágenes a la vez?`,
        answer: `Sí, puedes subir y ${verbLower} múltiples imágenes simultáneamente y descargarlas todas en un ZIP.`,
      },
      {
        question: '¿Es seguro usar esta herramienta?',
        answer: 'Sí, completamente. Todo el procesamiento ocurre en tu navegador. Tus imágenes nunca se envían a ningún servidor.',
      },
    ],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 2 templates — crop, rotate, resize-percent, batch
// ═══════════════════════════════════════════════════════════════════════════

interface TemplateContent {
  title: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  subtitle: string;
  introParagraph: string;
  seoIntro: string;
  howItWorksSteps: HowItWorksStep[];
  benefits: Benefit[];
  faqItems: FaqItem[];
}

export function cropTemplate(aspectRatio?: string, platform?: string): TemplateContent {
  const ratioLabel = aspectRatio ? ` (${aspectRatio})` : '';
  const contextLabel = platform ? ` para ${platform}` : aspectRatio ? ` en formato ${aspectRatio}` : '';
  const contextDesc = platform || (aspectRatio ? `formato ${aspectRatio}` : 'cualquier proporción');

  return {
    title: `Recortar imagen${contextLabel}`,
    h1: `Recortar imagen${contextLabel} online gratis`,
    metaTitle: `Recortar Imagen${contextLabel} Online Gratis | CompresorJPEG`,
    metaDescription: `Recorta tus imágenes${contextLabel} online gratis. Selecciona el área exacta, elige proporción${ratioLabel} y descarga al instante. Sin registro.`,
    subtitle: `Selecciona y recorta el área exacta de tu imagen${contextLabel}.`,
    introParagraph: `¿Necesitas recortar una imagen${contextLabel}? Nuestra herramienta te permite seleccionar visualmente el área que deseas conservar, con presets de proporción${ratioLabel} y recorte libre. Todo se procesa en tu navegador de forma instantánea.`,
    seoIntro: `Recortar imágenes${contextLabel} es una tarea común en diseño, redes sociales y documentos. Con nuestra herramienta puedes recortar cualquier imagen JPG, PNG o WebP directamente en el navegador, sin instalar software ni subir archivos a un servidor. Ideal para ${contextDesc}.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tu imagen', description: 'Arrastra y suelta la imagen que quieres recortar.' },
      { number: '2', title: 'Selecciona el área', description: `Dibuja el recorte sobre la imagen${aspectRatio ? ` con proporción ${aspectRatio}` : ' con proporción libre o preestablecida'}.` },
      { number: '3', title: 'Descarga el resultado', description: 'Haz clic en "Recortar" y descarga tu imagen recortada.' },
    ],
    benefits: [
      { title: 'Recorte visual preciso', description: 'Selecciona exactamente el área que quieres conservar con controles visuales intuitivos.' },
      { title: 'Proporciones preestablecidas', description: 'Elige entre proporciones como 1:1, 16:9, 4:3 o recorte libre según tu necesidad.' },
      { title: 'Procesamiento local', description: 'Tu imagen se recorta en tu navegador sin subirse a ningún servidor.' },
      { title: 'Sin registro necesario', description: 'Recorta imágenes de forma gratuita sin crear una cuenta.' },
    ],
    faqItems: [
      {
        question: `¿Cómo recorto una imagen${contextLabel}?`,
        answer: `Sube tu imagen, selecciona el área de recorte${aspectRatio ? ` con proporción ${aspectRatio}` : ''} y haz clic en "Recortar". Descarga el resultado al instante.`,
      },
      {
        question: '¿Se pierde calidad al recortar?',
        answer: 'No. El recorte simplemente elimina las áreas no seleccionadas sin recomprimir la imagen, manteniendo la calidad original.',
      },
      {
        question: '¿Qué formatos puedo recortar?',
        answer: 'Puedes recortar imágenes en formato JPG, PNG y WebP.',
      },
      {
        question: '¿Puedo elegir una proporción personalizada?',
        answer: 'Sí, puedes usar el modo libre para recortar en cualquier proporción, o elegir entre presets como 1:1, 16:9, 4:3 y 9:16.',
      },
    ],
  };
}

export function rotateTemplate(keyword: string, displayKeyword: string, rotatePreset?: string): TemplateContent {
  const presetLabel = rotatePreset === '90' ? ' 90 grados'
    : rotatePreset === '180' ? ' 180 grados'
    : rotatePreset === 'flip-h' ? ' horizontalmente'
    : rotatePreset === 'flip-v' ? ' verticalmente'
    : '';

  return {
    title: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)}${presetLabel}`,
    h1: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)}${presetLabel} online gratis`,
    metaTitle: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)}${presetLabel} Online Gratis | CompresorJPEG`,
    metaDescription: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)}${presetLabel} online gratis. Rota, gira y voltea imágenes JPG, PNG y WebP al instante. Sin registro.`,
    subtitle: `Rota, gira o voltea tu imagen de forma rápida y gratuita.`,
    introParagraph: `¿Necesitas ${displayKeyword}${presetLabel}? Con nuestra herramienta puedes aplicar rotaciones de 90°, 180°, 270° y voltear horizontal o verticalmente. Combina varias transformaciones y deshaz los cambios fácilmente.`,
    seoIntro: `Rotar y voltear imágenes es una necesidad frecuente al editar fotos tomadas con el teléfono en orientación incorrecta, preparar imágenes para impresión o adaptar fotos para redes sociales. Nuestra herramienta procesa todo en tu navegador de forma instantánea.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tu imagen', description: 'Arrastra y suelta la imagen que quieres rotar o voltear.' },
      { number: '2', title: 'Aplica transformaciones', description: `Usa los botones para rotar${presetLabel || ', girar o voltear'} tu imagen. Puedes combinar varias transformaciones.` },
      { number: '3', title: 'Descarga el resultado', description: 'Descarga tu imagen transformada lista para usar.' },
    ],
    benefits: [
      { title: 'Rotación y volteo instantáneos', description: 'Aplica rotaciones de 90°, 180°, 270° y volteo horizontal o vertical con un clic.' },
      { title: 'Transformaciones combinables', description: 'Aplica múltiples transformaciones y deshaz cada paso con el botón de deshacer.' },
      { title: 'Procesamiento local', description: 'Tu imagen se transforma en tu navegador sin subirse a ningún servidor.' },
      { title: 'Sin pérdida de calidad', description: 'La rotación se aplica sin recomprimir, manteniendo la calidad original.' },
    ],
    faqItems: [
      {
        question: `¿Cómo puedo ${displayKeyword}${presetLabel}?`,
        answer: `Sube tu imagen y haz clic en el botón correspondiente para ${displayKeyword}${presetLabel}. Puedes aplicar varias transformaciones y deshacer cualquier cambio.`,
      },
      {
        question: '¿Se pierde calidad al rotar una imagen?',
        answer: 'No, la rotación se realiza sin pérdida de calidad. La imagen se transforma en el canvas del navegador manteniendo la resolución original.',
      },
      {
        question: '¿Puedo combinar rotación y volteo?',
        answer: 'Sí, puedes aplicar múltiples transformaciones en secuencia. Usa el botón "Deshacer" para revertir el último cambio o "Restablecer" para volver al original.',
      },
      {
        question: '¿Qué formatos de imagen puedo rotar?',
        answer: 'Puedes rotar imágenes en formato JPG, PNG y WebP.',
      },
    ],
  };
}

export function resizePercentTemplate(percent: number, displayKeyword: string): TemplateContent {
  const isEnlarge = percent > 100;
  const verb = isEnlarge ? 'Aumentar' : 'Reducir';
  const verbLower = isEnlarge ? 'aumentar' : 'reducir';
  const percentLabel = `${percent}%`;

  return {
    title: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)}`,
    h1: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} online gratis`,
    metaTitle: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} Online Gratis | CompresorJPEG`,
    metaDescription: `${verb} el tamaño de tus imágenes al ${percentLabel} online gratis. Redimensiona JPG, PNG y WebP por porcentaje. Sin registro.`,
    subtitle: `${verb} las dimensiones de tu imagen al ${percentLabel} de su tamaño original.`,
    introParagraph: `¿Necesitas ${verbLower} el tamaño de una imagen al ${percentLabel}? Nuestra herramienta calcula automáticamente las nuevas dimensiones y redimensiona tu imagen manteniendo la proporción original. Funciona con JPG, PNG y WebP.`,
    seoIntro: `Redimensionar imágenes por porcentaje es útil cuando necesitas ${verbLower} proporcionalmente todas las dimensiones sin calcular píxeles exactos. Ideal para preparar thumbnails, adaptar imágenes para email o ${isEnlarge ? 'ampliar fotos para presentaciones' : 'reducir fotos para web'}.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tu imagen', description: 'Arrastra y suelta la imagen que quieres redimensionar.' },
      { number: '2', title: 'Elige el porcentaje', description: `Selecciona ${percentLabel} o introduce un porcentaje personalizado.` },
      { number: '3', title: 'Descarga el resultado', description: 'Descarga tu imagen redimensionada lista para usar.' },
    ],
    benefits: [
      { title: 'Redimensionado proporcional', description: 'Mantiene la proporción original de la imagen al redimensionar por porcentaje.' },
      { title: 'Presets rápidos', description: 'Elige entre porcentajes preestablecidos (25%, 50%, 75%, 125%, 150%) o introduce uno personalizado.' },
      { title: 'Procesamiento local', description: 'Tu imagen se redimensiona en tu navegador sin subirse a ningún servidor.' },
      { title: 'Vista previa instantánea', description: 'Visualiza las dimensiones finales antes de descargar el resultado.' },
    ],
    faqItems: [
      {
        question: `¿Cómo ${verbLower} una imagen al ${percentLabel}?`,
        answer: `Sube tu imagen, selecciona el preset de ${percentLabel} y haz clic en "Redimensionar". Las dimensiones se calculan automáticamente.`,
      },
      {
        question: '¿Se pierde calidad al redimensionar?',
        answer: isEnlarge
          ? 'Al aumentar el tamaño, puede haber una ligera pérdida de nitidez ya que se interpolan nuevos píxeles. Para mejores resultados, no superes el 200%.'
          : 'Al reducir, la calidad se mantiene excelente ya que se eliminan píxeles sin necesidad de interpolar.',
      },
      {
        question: '¿Puedo usar un porcentaje personalizado?',
        answer: 'Sí, además de los presets puedes introducir cualquier porcentaje entre 10% y 200%.',
      },
      {
        question: '¿Qué formatos de imagen puedo redimensionar?',
        answer: 'Puedes redimensionar imágenes en formato JPG, PNG y WebP.',
      },
    ],
  };
}

export function batchTemplate(displayKeyword: string, toolTypeLabel: string): TemplateContent {
  return {
    title: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)}`,
    h1: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} online gratis`,
    metaTitle: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} a la Vez Online Gratis | CompresorJPEG`,
    metaDescription: `${displayKeyword.charAt(0).toUpperCase() + displayKeyword.slice(1)} a la vez online gratis. Sube múltiples archivos, comprime en lote y descarga todo en un ZIP. Sin registro.`,
    subtitle: `Sube varias imágenes y ${toolTypeLabel} todas a la vez.`,
    introParagraph: `¿Necesitas ${displayKeyword} de una sola vez? Nuestra herramienta de procesamiento en lote te permite subir múltiples archivos, procesarlos simultáneamente y descargar todos los resultados en un único archivo ZIP. Ideal para ahorrar tiempo cuando tienes muchas imágenes que procesar.`,
    seoIntro: `El procesamiento en lote de imágenes es esencial para fotógrafos, diseñadores y gestores de contenido que trabajan con grandes volúmenes de archivos. Nuestra herramienta procesa cada imagen de forma paralela en tu navegador, sin límite de archivos y sin enviar nada a servidores externos.`,
    howItWorksSteps: [
      { number: '1', title: 'Sube tus imágenes', description: 'Arrastra y suelta múltiples imágenes o selecciona varios archivos a la vez.' },
      { number: '2', title: 'Procesamiento automático', description: `Cada imagen se ${toolTypeLabel} automáticamente con la configuración óptima.` },
      { number: '3', title: 'Descarga en ZIP', description: 'Descarga todas las imágenes procesadas en un único archivo ZIP.' },
    ],
    benefits: [
      { title: 'Procesamiento en lote', description: 'Sube y procesa decenas de imágenes simultáneamente sin esperas.' },
      { title: 'Descarga en ZIP', description: 'Descarga todos los resultados en un único archivo ZIP para mayor comodidad.' },
      { title: 'Procesamiento local', description: 'Todas las imágenes se procesan en tu navegador sin subirse a ningún servidor.' },
      { title: 'Progreso individual', description: 'Visualiza el estado de cada imagen y descárgalas individualmente si lo prefieres.' },
    ],
    faqItems: [
      {
        question: `¿Cuántas imágenes puedo ${toolTypeLabel} a la vez?`,
        answer: 'No hay un límite fijo. Puedes subir tantas imágenes como tu dispositivo pueda manejar. Recomendamos lotes de hasta 50 imágenes para un rendimiento óptimo.',
      },
      {
        question: '¿Se mantiene la calidad en el procesamiento en lote?',
        answer: 'Sí, cada imagen se procesa individualmente con el mismo algoritmo de alta calidad que nuestras herramientas individuales.',
      },
      {
        question: '¿Puedo descargar imágenes individuales?',
        answer: 'Sí, puedes descargar cada imagen procesada individualmente o todas juntas en un archivo ZIP.',
      },
      {
        question: '¿Es seguro procesar múltiples imágenes?',
        answer: 'Sí, completamente. Todo el procesamiento ocurre en tu navegador. Ninguna imagen se sube a servidores externos.',
      },
    ],
  };
}
